import React from 'react';
import _ from 'lodash';
import { FlexDeleteCommandArgs, FlexDeleteQuery, RepoItem } from '../../autogenerated/ZoomBridge';
import ConciseAsset from '../../common/ConciseAsset';
import Modal from '../Modal2';
import ThumbnailUtil from '../../util/ThumbnailUtil';
import ErrorId from '../../protocol/ErrorId';
import { toast } from '../../store/StoreUtil';
import t from '../../i18n/I18n';
import { KeyCode } from '../../util/Constants';
import KeydownConfig from '../../keyboard-shortcuts/KeydownConfig';
import KeyboardShortcuts from '../../keyboard-shortcuts/KeyboardShortcuts';
import SaveSettings from '../../queries/settings/SaveSettings';
import { filterAssets } from '../../util/SelectedItemUtil';
import PurgeQuery from '../../queries/purge/PurgeQuery';
import ErrorHandler from '../../error/ErrorHandler';

const debug = require('../../debug/debug')('wc:modals:DeleteAssetsModal');

type Props = {
  assets: RepoItem[],
  onClose: Function,
  message?: String,
  onDeleteSuccess: Function
};

export default class DeleteAssetsModal extends React.Component<Props> {
  static defaultProps = {
    onDeleteSuccess: _.noop
  };

  componentDidMount() {
    this.addEventListener();
  }

  componentWillUnmount() {
    this.removeEventListener();
  }

  removeEventListener = () => {
    KeyboardShortcuts.remove('DELETE_ASSETS_MODAL');
  };

  addEventListener = () => {
    KeyboardShortcuts.add(new KeydownConfig('DELETE_ASSETS_MODAL', this.deleteSelectedAssets, [KeyCode.ENTER]));
    KeyboardShortcuts.add(new KeydownConfig('DELETE_ASSETS_MODAL', this.props.onClose, [KeyCode.ESCAPE]));
  };

  render() {
    const { assets, onClose, message, actionLabel } = this.props;
    if (!assets.length) {
      return null;
    }
    if(!SaveSettings.getBoolean(SaveSettings.ASK_DELETE_CONFIRMATION)) {
      this.deleteSelectedAssets();
      return null;
    }

    const modalProps = {
      name: 'DeleteAssetsModal',
      title: t('CONFIRM'),
      onClose: onClose,
      buttons: [
        { label: t('CANCEL_LABEL'), onClick: onClose, type: 'CANCEL' },
        { label: actionLabel ? actionLabel : t('YES'), onClick: this.deleteSelectedAssets, type: 'PRIMARY' }
      ]
    };
    const confirmMessage = message ? message : t('CONFIRM_DELETE');
    const pluralized = assets.length === 1 ? t('ASSET_SMALL') : t('ASSETS_SMALL');
    return (
      <Modal {...modalProps} closeOnEsc closeOnOutsideClick>
        <div>
          <div style={{ marginBottom: '10px' }}>{t(confirmMessage, { length: assets.length, word: pluralized })}</div>
          <div style={{ maxHeight: `${window.innerHeight - 450}px`, overflowY: 'auto' }}>
            {assets.map((asset) => (
              <ConciseAsset key={asset.getFuid()} style={styles.assetDisplay} asset={asset} />
            ))}
          </div>
        </div>
      </Modal>
    );
  }

  purgeSelectedAssets = (assets): void => {
    let { assetIds } = filterAssets(assets, -1, false, true);
    if (assetIds && assetIds.length > 0) {
      new PurgeQuery(this.props.getProgressOnQuery).run(assets, assetIds);
    }
    this.props.onClose();
  };

  deleteSelectedAssets = () => {
    const { assets, onDeleteSuccess, onClose, actionLabel } = this.props;
    if (actionLabel && actionLabel === t('PURGE_MENU')) {
      this.purgeSelectedAssets(assets);
      return;
    } 

    const query = new FlexDeleteQuery();
    const deleteMessage = new FlexDeleteCommandArgs();
    deleteMessage.setLogMsg('Deleting file at server.');
    deleteMessage.setBranch('head');
    const remotePaths = ThumbnailUtil.computeRemotePathOfAssets(assets);
    deleteMessage.setInfiles(remotePaths);
    deleteMessage.setIncludeSmartShare(SaveSettings.getBoolean(SaveSettings.SMART_LINK_INCLUDE_IN_DELETE));
    query.message = deleteMessage;

    debug('[deleteSelectedAssets()] running query: ', query);
    query.run().then(
      (resp) => {
        debug('[deleteSelectedAssets()] response: ', resp);
        onClose(true);
        onDeleteSuccess(assets);
      },
      (error: ErrorId) => {
        onClose(true);
        debug('[deleteSelectedAssets()] error: ', error);
        if (!error) {
          toast(t('REMOVE_MODAL.INVALID_STATE_TEXT'));
        } else {
          ErrorHandler.handleError(error);
          // toast(error.toString());
        }
      }
    );
  };
}

const styles = {
  assetDisplay: {
    marginTop: '0.5em'
  },
  img:{
    height:'32px',
    width:'32px',
    objectFit:'cover'
  }
};
